import { api } from "@/api";
import { publicAxios } from "@/api/public";
import {
  PayloadCommentInactivate,
  PayloadPostInactivate,
} from "@/modules/settings/types";
import { PayloadInvalidateToken } from "@/types";
import { AxiosResponse } from "axios";
import _ from "lodash";
import {
  PayloadAcceptTerms,
  PayloadAnswer,
  PayloadAuth,
  PayloadCA,
  PayloadComment,
  PayloadDenounce,
  PayloadDenounceComment,
  PayloadEditAnswer,
  PayloadEditComment,
  PayloadIsRead,
  PayloadOnboarding,
  PayloadReaction,
  PayloadReactionComment,
  PayloadSearchByTag,
  PayloadTimeline,
  PayloadTimelineFourNetwork,
} from "../types";
class API {
  static getToken(payload: PayloadAuth): Promise<AxiosResponse> {
    return publicAxios.post("/authentication/authenticate", payload);
  }
  static getTokenUser(payload: PayloadOnboarding): Promise<AxiosResponse> {
    return publicAxios.get(`/company/onboarding/${payload.id}`, {
      params: payload.params,
    });
  }
  static getTimeline(payload: any): Promise<AxiosResponse> {
    return api.get("/post/feed", { params: payload });
  }
  static getReactions(payload: string): Promise<AxiosResponse> {
    return api.get(`/post/${payload}/reactions`);
  }
  static getDenounce(): Promise<AxiosResponse> {
    return api.get("/denunciation/reasons");
  }
  static postDenounce(payload: PayloadDenounce): Promise<AxiosResponse> {
    return api.post("/denunciation/post", payload);
  }
  static postReactionPost(payload: PayloadReaction): Promise<AxiosResponse> {
    return api.post("/interaction/react-post", payload);
  }
  static getCurrentPost(payload: string): Promise<AxiosResponse> {
    return api.get(`/post/${payload}`);
  }
  static getCurrentDraft(): Promise<AxiosResponse> {
    return api.get(`/post-draft`);
  }
  static postComment(payload: PayloadComment): Promise<AxiosResponse> {
    return api.post("/interaction/comment", payload);
  }
  static postReactionComment(
    payload: PayloadReactionComment
  ): Promise<AxiosResponse> {
    return api.post("/interaction/react-comment", payload);
  }
  static postAnswer(payload: PayloadAnswer): Promise<AxiosResponse> {
    return api.post("/interaction/reply", payload);
  }
  static getReactionsComment(payload: string): Promise<AxiosResponse> {
    return api.get(`/post/comment/${payload}/reactions`);
  }
  static putComment(payload: PayloadEditComment): Promise<AxiosResponse> {
    return api.put(`/interaction/comment/${payload.id}`, payload);
  }
  static putAnswer(payload: PayloadEditAnswer): Promise<AxiosResponse> {
    return api.put(`/interaction/reply/${payload.id}`, payload);
  }
  static deleteComment(payload: string): Promise<AxiosResponse> {
    return api.delete(`/interaction/comment/${payload}`);
  }
  static postDenouncComment(
    payload: PayloadDenounceComment
  ): Promise<AxiosResponse> {
    return api.post("/denunciation/comment", payload);
  }
  static postInactivate(
    payload: PayloadPostInactivate
  ): Promise<AxiosResponse> {
    return api.post("/inactivation/post", payload);
  }
  static getTimelineUser(payload: PayloadTimeline): Promise<AxiosResponse> {
    return api.get(`/post/${payload.userId}/posts`, { params: payload });
  }
  static postInactivateComment(
    payload: PayloadCommentInactivate
  ): Promise<AxiosResponse> {
    return api.post("/inactivation/comment", payload);
  }
  static postRegisterUserCA(payload: PayloadCA): Promise<AxiosResponse> {
    return api.post(`/post/register-user-ca`, payload);
  }
  static getUpdateStatusVideo(payload: number): Promise<AxiosResponse> {
    return api.get(`/media/${payload}/set-video-is-played`);
  }
  static getRegisterUserPost(payload: number): Promise<AxiosResponse> {
    return api.get(`/post/metrics/${payload}`);
  }
  static putDeletePost(payload: number): Promise<AxiosResponse> {
    return api.put(`/post/inactivate/${payload}`);
  }
  static getPostAnswer(payload: number | string): Promise<AxiosResponse> {
    return api.get(`/post/answer/${payload}`);
  }
  static getFixPost(payload: any): Promise<AxiosResponse> {
    return api.get(`/post/fix-post/${payload.id}`, { params: payload.params });
  }
  static getFeed(payload: any): Promise<AxiosResponse> {
    return api.get("/post/api/v2/feed", {
      params: _.omit(payload, "headers"),
      headers: payload.headers,
    });
  }
  static getForYou(payload: any): Promise<AxiosResponse> {
    return api.get("post/api/v2/for-you", {
      params: _.omit(payload, "headers"),
      headers: payload.headers,
    });
  }
  static getTimelineFourNetwork(
    payload: PayloadTimelineFourNetwork
  ): Promise<AxiosResponse> {
    return api.get("/post/by-fournetwork", { params: payload });
  }
  static postReadingList(payload: PayloadIsRead): Promise<AxiosResponse> {
    return api.post(`/post/set-is-read${payload}`);
  }
  static getReadingList(payload: any): Promise<AxiosResponse> {
    return api.get("/post/get-reads", { params: payload });
  }
  static postDenunciationComment(payload: any): Promise<AxiosResponse> {
    return api.post("/denunciation/comment", payload);
  }
  static getCaseUser(payload: any): Promise<AxiosResponse> {
    return api.get(`/post/${payload.id}/success-case`, {
      params: payload.params,
    });
  }
  static getFixForYouPost(payload: any): Promise<AxiosResponse> {
    return api.get(`/post/fix-for-you-post/${payload.id}`, {
      params: payload.params,
    });
  }
  static putFeature(payload: string): Promise<AxiosResponse> {
    return api.put(`/feature/${payload}`, { params: payload });
  }
  static getSidebarItems(): Promise<AxiosResponse> {
    return api.get("/sidebar/timeline");
  }
  static getNewTimeline(payload: any): Promise<AxiosResponse> {
    return api.get("/post/api/v2/timeline", {
      params: _.omit(payload, "headers"),
      headers: payload.headers,
    });
  }
  static getBannerImages(): Promise<AxiosResponse> {
    return api.get("/banners/rotatings");
  }
  static getSearchByTag(payload: PayloadSearchByTag): Promise<AxiosResponse> {
    return api.get("/post/api/v2/interest", {
      params: payload,
    });
  }
  static patchInvalidateToken(
    payload: PayloadInvalidateToken
  ): Promise<AxiosResponse> {
    return api.patch("/login/refresh-token/invalidate", payload);
  }
  static getAcceptTerms(payload: PayloadAcceptTerms): Promise<AxiosResponse> {
    return api.get("/user/accept-term", { params: payload });
  }
  static getProfileFourNetwork(): Promise<AxiosResponse> {
    return api.get("/profile/by-fournetwork");
  }
}

export default API;
